import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, db } from './firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';
import './LinkedInTheme.css';

function HRDashboard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const fetchUserData = async () => {
      const currentUser = auth.currentUser;
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
            const data = userDoc.data();
            setUserProfile(data.profile || {});
        }
      }
    };
    fetchUserData();
  }, []);

  // Dashboard Action Items
  const dashboardItems = [
    {
      key: 'talent-pool',
      title: t('welcome.hrTitle'),
      subtitle: t('welcome.hrSubtitle'),
      icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25A1.125 1.125 0 0 1 9.75 19.875V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" /></svg>,
      onClick: () => navigate('/talent-pool')
    },
    {
      key: 'invite-employees',
      title: t('hrDashboard.inviteEmployeesTitle'),
      subtitle: t('hrDashboard.inviteEmployeesSubtitle'),
      icon: <svg fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" /></svg>,
      onClick: () => navigate('/invite-employees')
    }
  ];

  return (
    <div className="li-layout">
      <div className="li-container">
        
        {/* Left Sidebar: Profile */}
        <aside className="li-sidebar-left">
           <div className="li-card li-profile-card">
              <div className="li-card-background"></div>
              <div className="li-card-content">
                 <img 
                    src={user?.photoURL || `https://ui-avatars.com/api/?name=${encodeURIComponent(userProfile?.displayName || user?.email || 'User')}&background=random`} 
                    alt="Profile" 
                    className="li-avatar" 
                    onClick={() => navigate('/profile')}
                 />
                 <div className="li-identity">
                    <h3 className="li-name" onClick={() => navigate('/profile')} style={{cursor: 'pointer'}}>
                        {userProfile?.displayName || user?.email?.split('@')[0]}
                    </h3>
                    <p className="li-headline">{userProfile?.headline || t('welcome.subtitle')}</p>
                 </div>
              </div>
           </div>


        </aside>

        {/* Main Feed: Dashboard Content */}
        <main className="li-main-feed">
           
           {/* Welcome / Header Card */}
           <div className="li-card" style={{ padding: '16px' }}>
               <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '8px', color: 'rgba(0,0,0,0.9)' }}>
                   {t('hrDashboard.title')}
               </h2>
               <p style={{ fontSize: '14px', color: 'rgba(0,0,0,0.6)' }}>
                   {t('hrDashboard.subtitle')}
               </p>
           </div>

           {/* Dashboard Grid */}
           <div className="li-dashboard-grid">
              {dashboardItems.map(item => (
                <div key={item.key} className="li-dashboard-card" onClick={item.onClick}>
                   <div className="li-dashboard-icon">{item.icon}</div>
                   <h3 className="li-dashboard-title">{item.title}</h3>
                   <p className="li-dashboard-subtitle">{item.subtitle}</p>
                </div>
              ))}
           </div>

        </main>

        {/* Right Sidebar: Info / Promo */}
        <aside className="li-sidebar-right">
           <div className="li-card li-promo-card">
               <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px' }}>
                   {t('hrDashboard.talentIntelligenceTitle')}
               </div>
               <div style={{ fontSize: '12px', color: 'rgba(0,0,0,0.7)', marginBottom: '16px', lineHeight: '1.5' }}>
                   {t('hrDashboard.talentIntelligenceSubtitle')}
               </div>
                <div style={{ marginTop: '16px' }}>
                 <img 
                     src="/Flat_illutrations/report.png" 
                     alt={t('hrDashboard.analyticsReportAlt')} 
                     style={{ width: '100%', borderRadius: '4px' }} 
                     onError={(e) => e.target.style.display = 'none'}
                 />
               </div>
               <button className="li-btn-outline-primary" onClick={() => navigate('/talent-pool')} style={{ marginTop: '16px' }}>
                   {t('hrDashboard.exploreDataButton')}
               </button>
           </div>
           <div className="li-footer-links">
                <Link to="/about">{t('footer.about')}</Link>
                <Link to="/accessibility">{t('footer.accessibility')}</Link>
                <Link to="/help-center">{t('footer.helpCenter')}</Link>
                <Link to="/privacy-terms">{t('footer.privacyTerms')}</Link>
                <span>{t('auth.footer.rights')}</span>
           </div>
        </aside>

      </div>
    </div>
  );
}

export default HRDashboard;